import React, { useState, useMemo, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { POST_TYPES } from './constants';

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const MONTHS = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const CalendarView = ({ darkMode, posts, onView, getInstanceName, onNewPost, onDelete, onEdit }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const menuRef = useRef(null);
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // Close menu on click outside
  useEffect(() => {
    if (!contextMenu) return;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setContextMenu(null);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [contextMenu]);

  const handleDayClick = (day, dayPosts) => {
    setContextMenu(null);
    if (!day) return;
    if (dayPosts.length > 0) {
      setSelectedDay(selectedDay === day ? null : day);
    }
  };

  const handleContextMenu = (e, day, post) => {
    if (!day) return;
    e.preventDefault();
    e.stopPropagation();
    // Don't show empty menu (past day with no post)
    if (!post && !isFutureDay(day)) return;
    setContextMenu({ x: e.clientX, y: e.clientY, day, post: post || null });
  };

  const isFutureDay = (day) => {
    return new Date(year, month, day, 23, 59) >= new Date();
  };

  const prev = () => setCurrentDate(new Date(year, month - 1, 1));
  const next = () => setCurrentDate(new Date(year, month + 1, 1));
  const today = () => setCurrentDate(new Date());

  // Build calendar grid
  const calendarDays = useMemo(() => {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days = [];

    // Empty cells before first day
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(d);

    return days;
  }, [year, month]);

  // Group posts by day
  const postsByDay = useMemo(() => {
    const map = {};
    posts.forEach(p => {
      const date = p.published_at || p.scheduled_at || p.created_at;
      if (!date) return;
      const d = new Date(date);
      if (d.getFullYear() === year && d.getMonth() === month) {
        const day = d.getDate();
        if (!map[day]) map[day] = [];
        map[day].push(p);
      }
    });
    return map;
  }, [posts, year, month]);

  const todayDate = new Date();
  const isToday = (day) => day === todayDate.getDate() && month === todayDate.getMonth() && year === todayDate.getFullYear();

  const statusColor = (status) => {
    if (status === 'published') return 'bg-green-500';
    if (status === 'scheduled' || status === 'publishing') return 'bg-blue-500';
    if (status === 'failed') return 'bg-red-500';
    return 'bg-gray-400';
  };

  return (
    <div className={`rounded-xl border ${darkMode ? 'bg-zinc-800/50 border-gray-700' : 'bg-white border-gray-200'}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-zinc-700">
        <div className="flex items-center gap-2">
          <button onClick={prev} className={`p-1.5 rounded-lg transition ${darkMode ? 'hover:bg-zinc-700' : 'hover:bg-gray-100'}`}>
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h3 className={`text-lg font-semibold min-w-[200px] text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {MONTHS[month]} {year}
          </h3>
          <button onClick={next} className={`p-1.5 rounded-lg transition ${darkMode ? 'hover:bg-zinc-700' : 'hover:bg-gray-100'}`}>
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
        <button onClick={today} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${darkMode ? 'bg-zinc-700 text-gray-300 hover:bg-zinc-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
          Hoje
        </button>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7">
        {WEEKDAYS.map(d => (
          <div key={d} className={`py-2 text-center text-xs font-semibold ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{d}</div>
        ))}
      </div>

      {/* Days grid */}
      <div className="grid grid-cols-7">
        {calendarDays.map((day, i) => {
          const dayPosts = day ? (postsByDay[day] || []) : [];
          return (
            <div key={i} className={`min-h-[120px] p-1.5 border-t border-r ${
              darkMode ? 'border-zinc-700' : 'border-gray-100'
            } ${!day ? (darkMode ? 'bg-zinc-900/30' : 'bg-gray-50/50') : 'cursor-pointer'} ${
              selectedDay === day && day ? (darkMode ? 'bg-zinc-700/50' : 'bg-pink-50') : ''
            }`}
              onClick={() => handleDayClick(day, dayPosts)}
              onContextMenu={(e) => handleContextMenu(e, day)}>
              {day && (
                <>
                  <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-sm font-medium ${
                    isToday(day)
                      ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white'
                      : darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>{day}</span>
                  <div className="mt-1 space-y-1">
                    {dayPosts.slice(0, 3).map(p => {
                      const typeInfo = POST_TYPES.find(t => t.value === p.tipo);
                      const TypeIcon = typeInfo?.icon;
                      return (
                        <div key={p.id} onClick={(e) => { e.stopPropagation(); onView(p); }}
                          onContextMenu={(e) => handleContextMenu(e, day, p)}
                          className={`flex items-center gap-1 px-1.5 py-1 rounded text-xs cursor-pointer transition truncate ${
                            darkMode ? 'hover:bg-zinc-700' : 'hover:bg-gray-100'
                          }`}>
                          <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${statusColor(p.status)}`} />
                          {TypeIcon && <TypeIcon className="w-3 h-3 flex-shrink-0 text-gray-400" />}
                          <span className={`truncate ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            {typeInfo?.label} {getInstanceName(p.instancia_id) ? `· ${getInstanceName(p.instancia_id)}` : ''}
                          </span>
                        </div>
                      );
                    })}
                    {dayPosts.length > 3 && (
                      <span className={`text-xs px-1.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        +{dayPosts.length - 3} mais
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Day detail panel */}
      {selectedDay && postsByDay[selectedDay]?.length > 0 && (
        <div className={`border-t p-4 ${darkMode ? 'border-zinc-700' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between mb-3">
            <h4 className={`font-semibold text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {selectedDay} de {MONTHS[month]} — {postsByDay[selectedDay].length} post(s)
            </h4>
            <button onClick={() => setSelectedDay(null)} className={`p-1 rounded-lg transition ${darkMode ? 'hover:bg-zinc-700' : 'hover:bg-gray-100'}`}>
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {postsByDay[selectedDay].map(p => {
              const typeInfo = POST_TYPES.find(t => t.value === p.tipo);
              const TypeIcon = typeInfo?.icon;
              const date = new Date(p.published_at || p.scheduled_at || p.created_at);
              const instanceName = getInstanceName(p.instancia_id);
              return (
                <div key={p.id} onClick={() => onView(p)}
                  className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition ${
                    darkMode ? 'bg-zinc-800 border-zinc-700 hover:border-zinc-500' : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'
                  }`}>
                  <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-gradient-to-br from-pink-500/10 to-purple-600/10 flex items-center justify-center">
                    {p.cover_url ? (
                      <img src={p.cover_url} alt="" className="w-full h-full object-cover" />
                    ) : TypeIcon ? (
                      <TypeIcon className="w-6 h-6 text-gray-400" />
                    ) : null}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${statusColor(p.status)}`} />
                      <span className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{typeInfo?.label}</span>
                    </div>
                    {instanceName && (
                      <p className={`text-sm mt-1 ${darkMode ? 'text-pink-400' : 'text-pink-600'}`}>{instanceName}</p>
                    )}
                    <p className={`text-sm truncate mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {p.caption?.slice(0, 60) || 'Sem legenda'}
                    </p>
                    <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {/* Context Menu */}
      {contextMenu && (
          <div ref={menuRef} style={{ left: contextMenu.x, top: contextMenu.y }}
            className={`fixed z-50 py-1 rounded-lg shadow-xl border min-w-[180px] ${darkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-200'}`}>
            {isFutureDay(contextMenu.day) && (
              <button onClick={() => {
                const date = new Date(year, month, contextMenu.day, 10, 0);
                onNewPost(date.toISOString().slice(0, 16));
                setContextMenu(null);
              }} className={`w-full text-left px-4 py-2 text-sm transition ${darkMode ? 'text-gray-300 hover:bg-zinc-700' : 'text-gray-700 hover:bg-gray-100'}`}>
                ➕ Nova postagem
              </button>
            )}
            {contextMenu.post && (
              <>
                <button onClick={() => { onView(contextMenu.post); setContextMenu(null); }}
                  className={`w-full text-left px-4 py-2 text-sm transition ${darkMode ? 'text-gray-300 hover:bg-zinc-700' : 'text-gray-700 hover:bg-gray-100'}`}>
                  👁️ Ver detalhes
                </button>
                {['draft', 'scheduled', 'failed'].includes(contextMenu.post.status) && (
                  <>
                    <button onClick={() => { onEdit(contextMenu.post); setContextMenu(null); }}
                      className={`w-full text-left px-4 py-2 text-sm transition ${darkMode ? 'text-gray-300 hover:bg-zinc-700' : 'text-gray-700 hover:bg-gray-100'}`}>
                      ✏️ Editar
                    </button>
                    <button onClick={() => { onDelete(contextMenu.post.id); setContextMenu(null); }}
                      className={`w-full text-left px-4 py-2 text-sm text-red-500 transition ${darkMode ? 'hover:bg-zinc-700' : 'hover:bg-gray-100'}`}>
                      🗑️ Excluir
                    </button>
                  </>
                )}
              </>
            )}
          </div>
      )}
    </div>
  );
};

export default CalendarView;
